import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  breakGlass,
  decide,
  getGateMode,
  getInbox,
  getOutbox,
  getRequest,
  type ApprovalRequest,
} from "@/services/approvalsApi";
import { getAuthToken, getTenantId } from "@/services/apiClient";
import { ApiError } from "@/services/apiClient";

const INBOX_KEY = ["approvals", "inbox"] as const;
const OUTBOX_KEY = ["approvals", "outbox"] as const;

const isAuthed = () => typeof window !== "undefined" && !!getAuthToken();

export function useApprovalsInbox() {
  return useQuery({
    queryKey: INBOX_KEY,
    queryFn: getInbox,
    enabled: isAuthed(),
    staleTime: 30_000,
    refetchOnMount: true,
  });
}

export function useApprovalsOutbox() {
  return useQuery({
    queryKey: OUTBOX_KEY,
    queryFn: getOutbox,
    enabled: isAuthed(),
    staleTime: 30_000,
    refetchOnMount: true,
  });
}

export function useApprovalRequest(id: string | undefined) {
  return useQuery({
    queryKey: ["approvals", "request", id],
    queryFn: () => getRequest(id as string),
    enabled: !!id && isAuthed(),
    refetchInterval: (q) => {
      const data = q.state.data as ApprovalRequest | undefined;
      return data?.status === "PENDING" ? 15_000 : false;
    },
  });
}

export function useApprovalGateMode() {
  const tenantId = getTenantId();
  return useQuery({
    queryKey: ["approvals", "gate-mode", tenantId],
    queryFn: () => getGateMode(tenantId as string),
    enabled: !!tenantId && isAuthed(),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Pull a remediation hint out of the wrapped ApiError JSON, if any. */
function extractRemediation(err: unknown): string | null {
  if (err instanceof ApiError) {
    // ApiError swallows details, but message often contains the human text.
    return null;
  }
  return null;
}

interface DecideArgs {
  id: string;
  decision: "APPROVE" | "DENY";
  reason?: string;
}

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, reason }: DecideArgs) =>
      decide(id, decision, reason),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.decision === "APPROVE" ? "Approved" : "Denied"
      );
      qc.invalidateQueries({ queryKey: INBOX_KEY });
      qc.invalidateQueries({ queryKey: OUTBOX_KEY });
      qc.invalidateQueries({ queryKey: ["approvals", "request", vars.id] });
    },
    onError: (err: unknown) => {
      const msg =
        (err instanceof Error && err.message) ||
        "Couldn't record decision";
      const hint = extractRemediation(err);
      toast.error(hint ? `${msg} — ${hint}` : msg);
    },
  });
}

interface BreakGlassArgs {
  id: string;
  reason: string;
}

export function useBreakGlass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: BreakGlassArgs) => breakGlass(id, reason),
    onSuccess: (_d, vars) => {
      toast.success("Break-glass executed");
      qc.invalidateQueries({ queryKey: INBOX_KEY });
      qc.invalidateQueries({ queryKey: OUTBOX_KEY });
      qc.invalidateQueries({ queryKey: ["approvals", "request", vars.id] });
    },
    onError: (err: unknown) => {
      toast.error(
        (err instanceof Error && err.message) ||
          "Couldn't execute break-glass"
      );
    },
  });
}
